import useMobileCheck from "./useMobileCheck";
import { useUserPreference } from "./useUserPreference";

/** Tailwind-safe grid class names so JIT includes them */
const GRID_COLS_CLASSES = {
	1: "grid-cols-1",
	2: "grid-cols-2",
	3: "grid-cols-3",
	4: "grid-cols-4",
	5: "grid-cols-5",
	6: "grid-cols-6",
};

/** Shell widths from styles.css. Mobile drawers overlay the page and take no content width. */
const SIDEBAR_EXPANDED = 240; // .sidebar-wrapper w-60
const SIDEBAR_COLLAPSED = 64; // .sidebar-wrapper.collapsed w-16
const SIDEPANE_WIDTH = 304; // .sidepane-wrapper 19rem

/**
 * Column count from the width the cards actually have.
 * Each step keeps a card wide enough for a title, date, and status badge.
 */
export function columnsForContentWidth(contentWidth) {
	const width = Number(contentWidth) || 0;
	if (width < 680) return 1;
	if (width < 1000) return 2;
	if (width < 1400) return 3;
	if (width < 1800) return 4;
	if (width < 2200) return 5;
	return 6;
}

/** Viewport width left after the sidebar and notifications pane. */
export function contentWidthForGrid(windowSize, { sidebar = false, sidePane = false, isMobile = false, considerSidebar = false } = {}) {
	const width = typeof windowSize === "number" ? windowSize : 1024;
	if (!considerSidebar || isMobile) return width;
	const sidebarWidth = sidebar ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;
	const paneWidth = sidePane ? SIDEPANE_WIDTH : 0;
	return Math.max(0, width - sidebarWidth - paneWidth);
}

/**
 * Shared responsive grid column count based on the content area, not the raw window.
 *
 * @param {Object} options
 * @param {number} [options.defaultCols=3] - Preferred columns on a normal desktop. Narrower content uses fewer. Wider content may use more.
 * @param {boolean} [options.considerSidebar=false] - Subtract the sidebar and notifications pane from the viewport.
 * @returns {{ gridSize: number, gridClassName: string, isNarrow: boolean }}
 */
function useResponsiveGridSize({ defaultCols = 3, considerSidebar = false } = {}) {
	const { isMobile, windowSize } = useMobileCheck();
	const { sidePane, sidebar } = useUserPreference();

	// change start by cursor
	const contentWidth = contentWidthForGrid(windowSize, { sidebar, sidePane, isMobile, considerSidebar });
	const isNarrow = contentWidth < 1000;
	const preferred = Math.min(6, Math.max(1, defaultCols));

	let gridSize = columnsForContentWidth(contentWidth);
	// A caller that asks for fewer columns still gets fewer on a normal desktop.
	// Narrow widths already step down. Very wide content can go above the preference.
	if (contentWidth < 1400) {
		gridSize = Math.min(gridSize, preferred);
	}
	// change end by cursor

	gridSize = Math.min(6, Math.max(1, gridSize));
	const gridClassName = GRID_COLS_CLASSES[gridSize] ?? GRID_COLS_CLASSES[preferred] ?? GRID_COLS_CLASSES[3];

	return { gridSize, gridClassName, isNarrow };
}

export default useResponsiveGridSize;
export { GRID_COLS_CLASSES };
